/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/media-has-caption */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import videojs from 'video.js';
import { hexToRgb, makeRandomString } from 'common/utils/utils';
import 'videojs-contrib-quality-levels';
import 'videojs-hls-quality-selector';
import './index.scss';
import './videojs-overlay';
import 'videojs-overlay/dist/videojs-overlay.css';
import "videojs-hotkeys";
import 'videojs-vr/dist/videojs-vr';

import { IS_PROXY_BASED_LICENSE_ISSUANCE, getDrmPlayerConfig, getSupportedDrmType } from 'common/utils/drm-utils';
import videojsContribEme from 'videojs-contrib-eme';
import 'videojs-contrib-dash';
import 'videojs-contrib-quality-menu';
import DrmVideoPlaybackErrorModal from '../drm-video-playback-error-modal';

videojs.registerPlugin('eme', videojsContribEme);

const Plugin = videojs.getPlugin('plugin');

class LoopPlugin extends Plugin {

   constructor(player, options) {
      super(player, options);



      player.on('ready', () => {
         player.customRepeat = false;
         const button = player.controlBar.addChild('button', {
            controlText: 'Loop',
            className: 'custom-loop-button',
            clickHandler: function(event) {
               player.customRepeat = !Boolean(player.customRepeat);
               if(player.customRepeat) {
                  button.$('.vjs-icon-placeholder').classList.remove('ams-icon-repeat-off');
                  button.$('.vjs-icon-placeholder').classList.add('ams-icon-repeat');
               } else {
                  button.$('.vjs-icon-placeholder').classList.add('ams-icon-repeat-off');
                  button.$('.vjs-icon-placeholder').classList.remove('ams-icon-repeat');
               }
            },
         });
         button.$('.vjs-icon-placeholder').classList.add('ams-icon');
         button.$('.vjs-icon-placeholder').classList.add('ams-icon-repeat-off');
      });
      player.on('ended', () => {
         if(player.customRepeat) {
            player.play();
         }
      });
   }
}

videojs.registerPlugin('loopPlugin', LoopPlugin);


const VideoJsHlsPlayer = ({
   src,
   poster,
   onVideoPlay,
   isAutoPlay,
   loop = true,
   userInfo,
   isVrPlaying,
   isDrmProtected,
   dashUri,
   getLicenseToken,
   getDrmVideoLicenseProxyUrl,
   isTokenBasedLicenseIssuance = false,
   screenWidth,
   authToken,
   isNextApp,
}) => {
   const [isOpenDrmVideoPlaybackErrorModal, setIsOpenDrmVideoPlaybackErrorModal] = useState(false);
   let videoRef = useRef(null)

   const videoId = `video-${ makeRandomString() }`;
   let playEventFired = false;
   let mountedRef = useRef(false)

   useEffect(() => {
      if(isNextApp && mountedRef?.current){
         return;
      }
      mountedRef.current = true;
      if(window.videoJsPlayer) {
         window.videoJsPlayer.dispose();
      }
      window.videoJsPlayer = videojs(videoId, {
         autoplay: isAutoPlay ? 'any' : false,
         // loop: loop,
         poster: poster,
         bigPlayButton: true,
         loadingSpinner: true,
         aspectRatio: '16:9',
         textTrackSettings: false,
         playbackRates: [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
         plugins: {
            loopPlugin: {},
            hotkeys: {
               alwaysCaptureHotkeys: true,
               seekStep: 10,
            },
         },
      });
      window.videoJsPlayer.on('play', function(e) {
         if(!playEventFired) {
            playEventFired = true;
            if(onVideoPlay) {
               onVideoPlay(e);
            }
         }
      });

      async function configureVideoPlayer() {
         if(isDrmProtected) {
            window.videoJsPlayer.ready(async () => {

               window.videoJsPlayer.eme();

               const supportedDrmType = await getSupportedDrmType()

               if(!supportedDrmType) {
                  setIsOpenDrmVideoPlaybackErrorModal(true)
                  return;
               }

               let licenseToken;
               if(!IS_PROXY_BASED_LICENSE_ISSUANCE || isTokenBasedLicenseIssuance) {
                  licenseToken = await getLicenseToken(supportedDrmType)
               }

               const drmPlayerConfig = await getDrmPlayerConfig({
                  supportedDrmType,
                  hlsUri: src,
                  dashUri,
                  licenseToken,
                  getDrmVideoLicenseProxyUrl: () => getDrmVideoLicenseProxyUrl(supportedDrmType),
                  isTokenBasedLicenseIssuance,
                  authToken,
               })
               window.videoJsPlayer.src(drmPlayerConfig);
            });
         } else {
            const playerConfig = {
               src,
               type: 'application/x-mpegURL',
            }
            window.videoJsPlayer.src(playerConfig);
         }
      }
      configureVideoPlayer()
      window.videoJsPlayer.qualityMenu();


      if(isVrPlaying && window.videoJsPlayer.vr){
         window.videoJsPlayer.vr({ projection: '360' });
      }

   }, []);

   useEffect(() => {
      if(!window.videoJsPlayer) return;

      window.videoJsPlayer.poster(poster);
      window.videoJsPlayer.trigger('posterchange');
   }, [poster])

   useEffect(() => {
      createOverlay()
   }, [screenWidth]);

   useEffect(() => {
      const interval = setInterval(() => {
         if(!userInfo?.show_member_info) return
         // let isModified = false
         // let element = document.getElementById('vjs-overlay-content')

         // if(
         //    Number(element.style.opacity) !== userInfo?.opacity / 100
         // ){
         //    isModified = true
         // }

         // const content = element?.innerText
         // const watermark = `${ userInfo.username || "" } ${ userInfo.email || "" }`

         // if(watermark !== content?.replaceAll('\n', ' ') || isModified) {
         //    return
         // }
         createOverlay()

      }, 3000); // Check every 5 second

      return () => clearInterval(interval);
   }, []);

   const createOverlay = () =>  {
      if(
         window.videoJsPlayer.overlay &&
        (userInfo?.username || userInfo?.email) &&
        videoRef?.current
      ) {
         const previewWidth = videoRef.current.clientWidth;
         const previewHeight = videoRef.current.clientHeight;

         if(!previewHeight || !previewWidth) return;

         const fontSizeScales = {
            small: 0.01,
            big: 0.0202,
            medium: 0.0151,
         };

         const fontSize = previewWidth * fontSizeScales?.[userInfo?.font_size];

         // Clear any existing overlays (optional depending on usage)
         window.videoJsPlayer.overlay({
            debug: true,
            overlays: [],
         });

         const br = userInfo?.username && userInfo?.email && !userInfo?.text_is_one_line ? "<br/>" : "";

         // Create overlay config with content
         const overlays = [
            {
               content: `
              <p
              style="
                color: ${ userInfo?.color };
                font-size: ${ fontSize }px;
                opacity: ${ userInfo?.opacity / 100 };
                width: 100%;
                background-color: ${ userInfo?.background_opacity ? hexToRgb(userInfo?.background_color, userInfo?.background_opacity / 100) : 'transparent' };
                border-radius: ${ userInfo?.background_opacity && userInfo?.background_shape === 'rounded' ? '6px' : '' };
                padding: ${ userInfo?.background_opacity ? '6px 10px !important' : '0 !important' };
                white-space: nowrap;
              "
              id='vjs-overlay-content'
              >
                ${ userInfo.username || "" } ${ br } ${ userInfo.email || "" }
              </p>
            `,
               align: userInfo?.static_position !== 'center' ? 'bottom-left' : "center-position",
               start: "play",
               // end: "pause",
            },
         ];

         // Reinitialize with the new overlays
         window.videoJsPlayer.overlay({
            debug: false, // turn off unless you're debugging
            overlays,
         });
      }
   }

   return (
      <>
         <div className='video-player-wrapper'>
            <video
               controls
               playsInline
               id={ videoId }
               ref={ videoRef }
               className='vjs-16-9 video-js vjs-default-skin vjs-big-play-centered'
            >
            </video>
         </div>
         {
            isOpenDrmVideoPlaybackErrorModal && (
               <DrmVideoPlaybackErrorModal
                  onCloseModal={ () => setIsOpenDrmVideoPlaybackErrorModal(false) }
               />
            )
         }
      </>
   );
};

VideoJsHlsPlayer.propTypes = {
   src: PropTypes.string,
   poster: PropTypes.string,
   onVideoPlay: PropTypes.func,
   isAutoPlay: PropTypes.bool,
   loop: PropTypes.bool,
   userInfo: PropTypes.object,
   isVrPlaying: PropTypes.bool,
   isDrmProtected: PropTypes.bool,
   dashUri: PropTypes.string,
   getLicenseToken: PropTypes.func,
   getDrmVideoLicenseProxyUrl: PropTypes.func,
   isTokenBasedLicenseIssuance: PropTypes.bool,
   screenWidth: PropTypes.number,
   isNextApp: PropTypes.bool,
   authToken: PropTypes.string,
};

export default VideoJsHlsPlayer;
